import { HttpClient } from '@angular/common/http';
import { AfterViewInit, Component, ElementRef, HostListener, NgZone, ViewChild, inject } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { MenuItem } from "primeng/api";
import { ContextMenu } from 'primeng/contextmenu';
import { firstValueFrom } from 'rxjs';
import { Config } from 'src/modules/app-template/models/config.model';
import { RestEndpoint } from 'src/modules/sm-base/models/rest-endpoint.model';
import { GuiUtils } from 'src/modules/utils/misc/gui-utils';
import { UserInteractionItem } from 'src/modules/utils/misc/user-interaction-item.model';
import { Arrays } from 'src/modules/utils/shared/arrays';
import { Objects } from "src/modules/utils/shared/objects";
import { Strings } from "src/modules/utils/shared/strings";
import { Utils } from 'src/modules/utils/shared/utils';
import { ComponentView } from '../../models/component-view.model';
import { MainAppService } from '../../services/main-app.service';
import { SmTimer } from 'src/modules/sm-base/models/sm-timer.model';

@Component({
    selector: 'app-main',
    styleUrls: ['./app-main.component.scss'],
    templateUrl: './app-main.component.html',
    standalone: false
})
export class AppMainComponent implements AfterViewInit {

    static instance: AppMainComponent;

    app = inject(MainAppService);
    http = inject(HttpClient);
    router = inject(Router);
    zone = inject(NgZone);

    inited = false;
    sidebarActive: boolean;
    activeSidebarTabIndex: number;
    browserSupported = true;
    privacyPolicyAccepted = false;
    privacyPolicy = false;
    privacyPolicyHtml: any;
    lastMouseDown: Date;
    currentComponentFullScreen = true;
    debugUnusedCssClassesTimer: SmTimer = null;
    
    @ViewChild(RouterOutlet) outlet!: RouterOutlet;
    @ViewChild("cm") cm: ContextMenu;
    @ViewChild('confirmPopupTarget') confirmPopupTargetRef: ElementRef;

    constructor() {
        AppMainComponent.instance = this;
        this.browserSupported = this.app.deviceDetector.browser() != "internetexplorer";
        this.privacyPolicy = Config.get().privacyPolicy;
    }

    onMouseMove: (e: PointerEvent) => void;

    ngOnInit(): void {
        this.zone.runOutsideAngular(() => {
            this.onMouseMove = (e: PointerEvent) => {
                this.app.mouseX = e.pageX;
                this.app.mouseY = e.pageY;
            };

            window.addEventListener('pointermove', this.onMouseMove, { passive: true });
        });
    }

    ngOnDestroy(): void { 
        window.removeEventListener('pointermove', this.onMouseMove) 
    }

    async ngAfterViewInit(): Promise<void> {
        if (!this.browserSupported) {
            return;
        }

        this.router.events.subscribe(() => {
            Promise.resolve().then(() => this.updateFullScreenStatus());
        });

        if (this.privacyPolicy) {
            this.privacyPolicyAccepted = this.app.auth.userDetails?.getDatabaseUser()?.privacyPolicyAccepted;
            this.privacyPolicyHtml = this.privacyPolicyAccepted ? "" : await firstValueFrom(this.http.get("assets/portal/templates/privacyPolicy.html", { responseType: "text"}));
        }

        GuiUtils.angularTimer(() => {
            this.inited = true;
        });
    }

    ngDoCheck(): void {
        this.app.changeDetectionCount++;
    }

    onHotkeyInspectComponent(): void {
        if ((window as any).ng == null) {
            console.log("Das funktioniert nur im Debug-Modus. window.ng ist nicht definiert.");
            return;
        }

        let nodes = document.querySelectorAll(":hover");
        // eslint-disable-next-line @typescript-eslint/prefer-for-of
        for (let i = 0; i < nodes.length; i++) {
            let p = (window as any).ng.getComponent(nodes[i]);
            if (p != null) {
                console.log(p);
            }
        }
    }

    onHotkeyDeveloper(): void {
        let model: MenuItem[] = [
            {
                label: "Erweitere Dev-Optionen",
                badge: this.app.advancedDeveloper ? "AN" : "AUS",
                command: () => {
                    this.app.advancedDeveloper = !this.app.advancedDeveloper;
                }
            },
            {
                label: "Sidebar-Anzeige umschalten",
                badge: this.app.showSidebar ? "AN" : "AUS",
                command: () => {
                    this.app.showSidebar = !this.app.showSidebar;
                }
            },
            {
                label: "Angular-Routen debuggen",
                badge: this.app.debugRoutes ? "AN" : "AUS",
                command: () => {
                    this.app.debugRoutes = !this.app.debugRoutes;
                }
            },
            {
                label: "Fokus verfolgen",
                badge: this.app.useFocusTracker ? "AN" : "AUS",
                command: () => {
                    this.app.setUseFocusTracker(!this.app.useFocusTracker);
                }
            },
            {
                label: "Dark-Mode an/aus",
                badge: this.app.darkMode === true ? "AN" : this.app.darkMode === false ? "AUS" : "AUTO",
                command: () => {
                    this.app.toggleDarkMode(true);
                }
            },
            {
                label: "Interaktions-Historie auf Konsole ausgeben",
                command: () => {
                    console.log("\r\n"+ Arrays.itemsToString(Arrays.reverse(Arrays.sort(this.app.userInteractionHistory)).map(item => item.toString()), "\r\n"));
                }
            },
            {
                label: "Fehler werfen",
                command: () => {
                    throw new Error();
                }
            },
            {
                label: "Nicht verwendete CSS-Klassen finden",
                badge: this.debugUnusedCssClassesTimer != null ? "AN" : "AUS",
                command: () => {
                    if (this.debugUnusedCssClassesTimer != null) {
                        this.debugUnusedCssClassesTimer.destroy();
                        this.debugUnusedCssClassesTimer = null;
                    }
                    else {
                        this.debugUnusedCssClassesTimer = new SmTimer("debugUnusedCssClassesTimer", 0, 1000, () => this.debugUnusedCssClasses(), true);
                    }
                }
            },
            {
                label: "Angular ChangeDetection Debugging",
                command: () => {
                    let oldConsole = window.console.log;
                    window.console.log = (data: any[]) => {
                        oldConsole(data);
                        if (Strings.is(data)) {
                            data = [data];
                        }
                        if (data.length > 0 && Strings.is(data[0]) && (data[0] as string).endsWith("ms per check")) {
                            this.app.showToast("info", "Change Detection Analyse", data[0] as string);
                        }
                    };
                    Utils.setTimerOnce(1000, () => {
                        window.console.log = oldConsole;
                    });
                    (window as any).ng.profiler.timeChangeDetection();
                }
            },
            {
                label: "Angular ChangeDetection Debugging Periodic",
                command: () => {
                   Utils.setTimer(1000, () => console.log("Anzahl ChangeDetections: " + this.app.changeDetectionCount));
                }
            },
            {
                label: "Interne Prüfungen starten",
                command: async () => {
                    let result = await RestEndpoint.main().run("api/app/runInternalChecks").listStrings();
                    this.app.dialog.textArea("Interne Prüfungen", Arrays.itemsToString(result, "\r\n"), null, true);
                }
            }
        ];
        this.app.openContextMenu(model);
    }

    debugUnusedCssClasses(): void {
        let allowedClasses = [
            'burger-menu', 
            'desktop-menu', 
            "p-inputwrapper", 
            "p-password-unmask-icon", 
            "p-button-icon-left", 
            'p-breadcrumb-home-item', 
            'p-avatar-label', 
            'p-inputwrapper-filled', 
            'p-multiselect-dropdown-icon', 
            'p-tabpanel-active', 
            'p-select-dropdown-icon', 
            'p-menuitem-link-active',
            'p-filled', 
            'p-anchored-overlay-leave-to',
            'p-anchored-overlay-enter-to',
            'p-panel-icons', 
            'p-panel-icons-end',
            "p-tag-label",
            "p-chip-label",
            "p-tree-selectable",
            "p-tree-node-toggle-icon",
            "p-dialog-mask",
            "p-hidden-focusable",
            "p-iconfield-left",
            "p-tree-empty-message",
            "p-tree-node-checkbox",
            "p-message-undefined",
            "p-element",
            "cdk-drop-list",
            "cdk-drag",
            "cdk-drag-disabled",
            "ace_print-margin-layer",
            "ace_open",
            "ace_lparen",
            "ace_rparen",
            "ace_escape",
            'null',
            "undefined"            
        ];
        let found = [...(document.querySelectorAll('*') as unknown as any[])]
            .flatMap(el => [...el.classList])
            .filter((cls, i, arr) => arr.indexOf(cls) === i) 
            .filter(cls => {
                return !cls.startsWith("ng-") && !cls.startsWith("ag-") && !cls.startsWith("ace-") && 
                !allowedClasses.includes(cls) && ![...(document.styleSheets as unknown as any[])].some(sheet =>
                [...sheet.cssRules || []].some(rule =>
                    rule.selectorText && rule.selectorText.includes('.' + cls)
                )
                );
            });
        if (found.length > 0) {
            console.log(Arrays.itemsToString(found));
            this.app.showInfoToast(Arrays.itemsToString(found));
        }
    }

    onHotkeyOnSelection(): void {
        if (this.app.onSelectionCallback != null) {
            this.app.onSelectionCallback();
        }
    }

    @HostListener('document:click', ['$event'])
    async onClick(event: Event): Promise<void> {
        if (event.target != null) {
            let debugString = event.target instanceof HTMLElement ? GuiUtils.getHtmlElementDebugString(event.target) : [Objects.toString(event.target)];
            this.app.userInteractionHistory.push(new UserInteractionItem(this.lastMouseDown ?? new Date(), "CLICK", debugString));
            this.lastMouseDown = null;
        }
        if (event.target instanceof HTMLAnchorElement && event.target.className === 'dynamic-router-link') {
            event.preventDefault();
            const route = event.target?.getAttribute('href');
            if (route) {
                await this.app.navigateTo([`/${route}`]);
            }
        }
    }

    @HostListener('document:mousedown', ['$event'])
    public onMouseDown(event: Event): boolean {
        this.app.userInteracted();

        if (event.target != null) {
            this.lastMouseDown = new Date();
        }
        return true;
     }

     public onKeyPress(event: KeyboardEvent): boolean {
        let key = this.app.userKeyPress(event);
      //  console.log(key);
        if (key == "SHIFT-ALT-Y") {
            this.onHotkeyInspectComponent();
            event.stopPropagation();
            event.preventDefault();
        }
        else if (key == "SHIFT-ALT-D") {
            this.onHotkeyDeveloper();
            event.stopPropagation();
            event.preventDefault();
        }
        else if (key == "SHIFT-ALT-C") {
            this.onHotkeyOnSelection();
            event.stopPropagation();
            event.preventDefault();
        }
        return true;
     }

     @HostListener('document:wheel', ['$event'])
     public onWheel(_: Event): boolean {
        this.app.userInteracted();
        return true;
     }

     @HostListener('document:touchstart', ['$event'])
     public onTouchStart(_: Event): boolean {
        this.app.userInteracted();
        return true;
     }

     @HostListener('document:scroll', ['$event'])
     public onScroll(_: Event): boolean {
        this.app.userInteracted();
        return true;
     }
    
    async onTabClick(event, index: number): Promise<void> {
        if (this.app.visibleMenuTabs[index].routerLink != null) {
            this.activeSidebarTabIndex = index;
            this.sidebarActive = false;
            await this.app.navigateTo(this.app.visibleMenuTabs[index].routerLink);
        }
        else if (this.activeSidebarTabIndex === index) {
            this.sidebarActive = !this.sidebarActive;
        }
        else {
            this.activeSidebarTabIndex = index;
            this.sidebarActive = true;
        }

        event.preventDefault();
    }

    closeSidebar(event: Event): void {
        this.sidebarActive = false;
        event.preventDefault();
    }

    onTopbarSubItemClick(event: Event): void {
        event.preventDefault();
    }

    isDesktop(): boolean {
        return window.innerWidth > 1024;
    }

    updateFullScreenStatus(): void {
        try {
            const activated = this.outlet?.component;
            this.currentComponentFullScreen = activated instanceof ComponentView && activated.fullScreenComponent;
        }
        catch (error) {
            //User befindet sich zum Beispiel im Login-Dialog
            if (error.message != "NG04012" && error.message != "NG04012: Outlet is not activated") {
                throw error;
            }
        }
    }

    async acceptPrivacyPolicy(): Promise<void> {
        let user = this.app.auth.userDetails?.getDatabaseUser();
        if (user != null) {
            user.privacyPolicyAccepted = true;
            await RestEndpoint.main().post().body(user).run("api/app/databaseUser/acceptPrivacyPolicy").getText();
        }
        this.privacyPolicyAccepted = true;
    }

    async declinePrivacyPolicy(): Promise<void> {
        let user = this.app.auth.userDetails?.getDatabaseUser();
        if (user != null) {
            await RestEndpoint.main().post().body(user).run("api/app/databaseUser/declinePrivacyPolicy").getText();
        }
        await this.app.auth.logout();
    }


}